import type { ReactElement } from 'react';
import { Button, ButtonLink } from './Button';
import { cx } from './cx';
import { EmptyArt, type EmptyArtName } from './EmptyArt';
import { Icon } from './Icon';
import s from './EmptyState.module.css';

/** The single next step an empty state may offer: a real action or "How it works". */
export type EmptyStateAction =
  | { label: string; href: string; onPress?: never }
  | { label: string; onPress: () => void; href?: never };

interface EmptyStateBase {
  /** Says what's true: "No awards yet", "Nothing waiting on you". */
  headline: string;
  /** One sentence on what will appear here and when. */
  body: string;
  /** At most one action. A list of actions does not type-check. */
  action?: EmptyStateAction;
  size?: 'md' | 'sm';
  onDark?: boolean;
  /** Heading level of the headline in the page outline. Default 3 (inside a card). */
  headingLevel?: 2 | 3 | 4;
  className?: string;
}

/** Nothing has happened yet: pick the art by meaning (award, people, calendar, …). */
interface FirstDay extends EmptyStateBase {
  variant: 'first-day';
  art: EmptyArtName;
}

/** Something must be set up first (for example a budget pool): greyed, links to where it's set. */
interface NotSet extends EmptyStateBase {
  variant: 'not-set';
  art: EmptyArtName;
}

/** The queue had work and it's all done: always the inbox-done art and a gold check. */
interface CaughtUp extends EmptyStateBase {
  variant: 'caught-up';
  art?: 'inbox-done';
}

export type EmptyStateProps = FirstDay | NotSet | CaughtUp;

/** A designed empty state. Never "No data", "N/A" or a bare dash. */
export function EmptyState(props: EmptyStateProps): ReactElement {
  const {
    headline,
    body,
    action,
    size = 'md',
    onDark = false,
    headingLevel = 3,
    className,
  } = props;
  const art: EmptyArtName = props.variant === 'caught-up' ? 'inbox-done' : props.art;
  const Heading = headingLevel === 2 ? 'h2' : headingLevel === 3 ? 'h3' : 'h4';
  return (
    <div
      className={cx(
        s.empty,
        size === 'sm' && s.sm,
        props.variant === 'not-set' && s.notSet,
        onDark && s.onDark,
        className,
      )}
      data-variant={props.variant}
    >
      <EmptyArt name={art} size={size} />
      <Heading className={s.headline}>
        {props.variant === 'caught-up' && (
          <span className={s.check}>
            <Icon name="check" size={14} strokeWidth={3} />
          </span>
        )}
        {headline}
      </Heading>
      <p className={s.body}>{body}</p>
      {action && (
        <div className={s.action}>
          {action.href !== undefined ? (
            <ButtonLink href={action.href} variant={onDark ? 'ghost-on-dark' : 'ghost'} size="sm">
              {action.label}
            </ButtonLink>
          ) : (
            <Button onPress={action.onPress} variant={onDark ? 'ghost-on-dark' : 'ghost'} size="sm">
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export interface EmptyPhotoProps {
  /** A muted people photo; decorative. */
  src: string;
  eyebrow: string;
  headline: string;
  body: string;
  headingLevel?: 2 | 3;
  /** One gold next step ("Recognize someone"). */
  action?: { label: string; href: string };
}

/** The photo pattern for celebration places (employee Home, My recognition). */
export function EmptyPhoto({
  src,
  eyebrow,
  headline,
  body,
  headingLevel = 3,
  action,
}: EmptyPhotoProps): ReactElement {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div className={s.photo}>
      <img src={src} alt="" />
      <div className={s.photoText}>
        <span className={cx('eyebrow', s.photoEyebrow)}>{eyebrow}</span>
        <Heading className={cx('disp', s.photoHeadline)}>{headline}</Heading>
        <p className={s.photoBody}>{body}</p>
        {action && (
          <div className={s.photoAction}>
            <ButtonLink href={action.href} variant="gold">
              {action.label}
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  );
}
